import { GuideLiteEntry, GuideBubble } from "../Guide/GuideLitePage";
/**
 * SoulumoLanding — the front door served on `soulumo.app`.
 *
 * Mar 3 2026 · Michael is buying the soulumo domain and wants a
 * proper landing that's not a section of Reclaim but Soulumo's own
 * home. Keep it quiet, cinematic, brand-perfect.
 *
 * Structure (short — the whole point):
 *   1. Hero          — orb + wordmark + one italic line + Begin
 *   2. What it does  — three quiet cards (Install · Overnight · Foundations)
 *   3. The frequency — the honest science chip
 *   4. Pricing       — "£16.99 first month · £6.99/month" (soft, one card)
 *   5. Sign-in       — a tiny link
 */
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SoulumoWord from "../../components/SoulumoWord";
import InstallOrb from "../../components/InstallOrb";
import SoulumoHeader from "../../components/SoulumoHeader";
import SoulumoFooter from "../../components/SoulumoFooter";
import FoundingLightPricing from "./FoundingLightPricing";
import FoundingLightReserveModal from "./FoundingLightReserveModal";
import { LaunchBanner } from "../../components/soulumoLaunch/LaunchBanner";

const TOK = {
  ink: "#050a17",
  bg: "radial-gradient(ellipse at 50% 20%, #131f38 0%, #0a132a 40%, #030710 85%)",
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.42)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.86)",
  goldMist: "rgba(224,169,122,0.22)",
};

export default function SoulumoLanding() {
  const nav = useNavigate();
  const isAuthed = typeof window !== "undefined" && !!localStorage.getItem("reclaim_token");
  const [showReserve, setShowReserve] = useState(false);
  const reduceMotion = useMemo(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const steps = [
    {
      k: "explain",
      t: "Explain",
      s: "Tell Soulumo what you'd like to change.",
    },
    {
      k: "agree",
      t: "Agree",
      s: "Soulumo Coach creates the words with you.",
    },
    {
      k: "record",
      t: "Record",
      s: "Use your own voice — or let Soulumo speak the words for you.",
    },
    {
      k: "create",
      t: "Create",
      s: "Soulumo builds your personalised installation — words, voice and journey woven together.",
    },
  ];

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="soulumo-landing"
    >
      {/* Soft breathing halo behind the hero */}
      <div
        className="pointer-events-none absolute"
        style={{
          top: "-14%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 620,
          height: 620,
          background:
            "radial-gradient(circle, rgba(224,169,122,0.28) 0%, rgba(224,169,122,0.08) 30%, transparent 65%)",
          filter: "blur(30px)",
          animation: reduceMotion ? undefined : "sl-breathe 10s ease-in-out infinite",
        }}
        aria-hidden
      />
      <style>{`
        @keyframes sl-breathe {
          0%, 100% { opacity: 0.85; transform: translateX(-50%) scale(1); }
          50%      { opacity: 1;    transform: translateX(-50%) scale(1.05); }
        }
      `}</style>

      {/* Top-bar — replaced Mar 6 2026 by the shared SoulumoHeader
          (adds Library, Why Soulumo, Account for authed users, and a
          mobile hamburger). */}
      <LaunchBanner />
      <div className="relative z-10 max-w-4xl mx-auto">
        <SoulumoHeader />
      </div>

      {/* Hero */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 pt-12 pb-12 flex flex-col items-center text-center">
        <div style={{ width: 240, height: 240 }}>
          <InstallOrb state="resting" size={240} />
        </div>

        <div className="mt-8">
          <SoulumoWord size="xl" />
        </div>

        <p
          className="mt-6 leading-tight max-w-2xl"
          style={{
            color: TOK.cream,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(1.5rem, 5vw, 2.35rem)",
            fontWeight: 300,
            letterSpacing: "-0.005em",
          }}
        >
          Re-architect your inner world — <span style={{ fontStyle: "italic", color: TOK.gold }}>in your own voice.</span>
        </p>

        <p
          className="mt-5 italic leading-relaxed max-w-xl"
          style={{
            color: TOK.creamDim,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(1.02rem, 2.4vw, 1.18rem)",
          }}
        >
          Personalised installations that weave your words, your voice and
          a journey of music and tones — helping you reshape the patterns
          influencing how you think, feel and live.
        </p>

        {!isAuthed && (
          <button
            onClick={() => nav(sessionStorage.getItem("demo_kind") === "golf" ? "/soulumo/demo?kind=golf" : "/soulumo/demo")}
            data-testid="soulumo-landing-demo"
            className="mt-9 flex items-center gap-3 rounded-full pl-2 pr-6 py-2 text-left transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(224,169,122,0.45)", backdropFilter: "blur(14px)", color: TOK.cream }}
          >
            <img src="/coaches/adelaida_calm.webp" alt="" className="w-11 h-11 rounded-full object-cover object-top" />
            <span>
              <span className="block" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.15rem" }}>See how it works with Adelaida</span>
              <span className="block text-[0.6rem] uppercase tracking-[0.24em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>Two-minute demo · No sign-up</span>
            </span>
          </button>
        )}

        <div className={`${isAuthed ? "mt-10" : "mt-6"} flex items-center gap-4`}>
          <button
            onClick={() => nav(isAuthed ? "/my-installations" : "/soulumo/join")}
            data-testid="soulumo-landing-begin"
            className="rounded-full px-7 py-3"
            style={{
              background: "rgba(224,169,122,0.18)",
              border: `1px solid ${TOK.gold}`,
              color: TOK.cream,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.75rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
          >
            · Begin ·
          </button>
          <button
            onClick={() => nav("/soulumo/why")}
            data-testid="soulumo-landing-why"
            className="text-[0.62rem] uppercase tracking-[0.28em]"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            Why Soulumo? →
          </button>
        </div>

        <button
          onClick={() => document.getElementById("sl-how")?.scrollIntoView({ behavior: "smooth" })}
          className="mt-6 text-[0.62rem] uppercase tracking-[0.28em]"
          style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
          data-testid="soulumo-landing-how-link"
        >
          How it works ↓
        </button>

        <div
          className="mt-8 text-[0.56rem] uppercase tracking-[0.4em]"
          style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
        >
          Cloned voice · Signature installs · Overnight
        </div>
      </section>

      {/* How Soulumo works — 4 steps */}
      <div className="relative z-10 max-w-md mx-auto px-6 pb-12"><GuideLiteEntry to="/soulumo/guide" label="Soulumo" /></div>
      <GuideBubble to="/soulumo/guide" label="Soulumo" />
      <section id="sl-how" className="relative z-10 max-w-3xl mx-auto px-6 pb-16">
        <div
          className="text-center text-[0.56rem] uppercase tracking-[0.4em]"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          How Soulumo works
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {steps.map((s, i) => (
            <div
              key={s.k}
              data-testid={`soulumo-landing-step-${s.k}`}
              className="rounded-2xl p-6 relative"
              style={{
                background: "rgba(245,236,217,0.03)",
                border: `1px solid ${TOK.goldMist}`,
              }}
            >
              <div
                className="absolute top-4 right-5 text-[0.6rem] uppercase tracking-[0.32em]"
                style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
              >
                0{i + 1}
              </div>
              <div
                className="font-light pr-8"
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "1.35rem",
                  color: TOK.cream,
                  lineHeight: 1.2,
                }}
              >
                {s.t}
              </div>
              <p
                className="mt-3 italic leading-relaxed text-[0.95rem]"
                style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
              >
                {s.s}
              </p>
            </div>
          ))}
        </div>

        {/* Closing line */}
        <div className="mt-12 text-center max-w-xl mx-auto">
          <div
            className="text-[0.56rem] uppercase tracking-[0.4em] mb-4"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            · The heart of it ·
          </div>
          <p
            className="italic leading-relaxed"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.15rem, 3vw, 1.5rem)",
              color: TOK.cream,
            }}
          >
            You didn't choose all of your original programming.
            <br />
            <span style={{ color: TOK.gold }}>But you can choose what you install next.</span>
          </p>
        </div>
      </section>

      {/* Frequency chip strip — honest science, no gimmick */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 pb-16 text-center">
        <div className="flex flex-wrap justify-center gap-2">
          {[
            "2 Hz · Delta · Deep sleep",
            "6 Hz · Theta · Receptive",
            "10 Hz · Alpha · Calm focus",
            "18 Hz · Beta · Active",
          ].map((label) => (
            <span
              key={label}
              className="rounded-full px-3 py-[4px] text-[0.55rem] uppercase tracking-[0.22em]"
              style={{
                background: "rgba(224,169,122,0.06)",
                border: `1px solid ${TOK.goldMist}`,
                color: TOK.goldSoft,
                fontFamily: '"Manrope", sans-serif',
              }}
            >
              {label}
            </span>
          ))}
        </div>
        <p
          className="mt-5 italic leading-relaxed max-w-xl mx-auto"
          style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.02rem" }}
        >
          Every install carries a specific state — printed clean on the player so you always know what you're installing.
        </p>
      </section>

      {/* ── Founding Light pricing ─────────────────────────────
         Big-anchor design (Feb 2026): £1.67 / month is the visual
         hero. Annual billing is the featured option; a soft toggle
         lets a visitor flip to £8.99 monthly if they prefer that
         cadence. Everything else — copy, glow, reassurance — is
         tuned to make the offer readable in two seconds. */}
      <section
        className="relative z-10 max-w-xl mx-auto px-6 pb-20"
        data-testid="soulumo-landing-founding-light"
      >
        <FoundingLightPricing
          isAuthed={isAuthed}
          onClaim={() => nav(isAuthed ? "/voice-clone" : "/soulumo/join?next=%2Fvoice-clone")}
        />

        <p
          className="mt-6 text-center italic text-[0.82rem] leading-relaxed max-w-md mx-auto"
          style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}
        >
          Soulumo is part of the Reclaim Group. Every subscription helps
          keep supported memberships open to people who cannot access them —
          through <button
            onClick={() => nav("/one-million-lights")}
            className="underline"
            style={{ color: TOK.goldSoft }}
            data-testid="soulumo-landing-oml-link"
          >One Million Lights</button>.
        </p>
      </section>

      {/* Footer — Mar 6 2026 · replaced the plain caption with the
          shared SoulumoFooter (Wellbeing · Support · Terms · Privacy). */}
      <SoulumoFooter />

      {showReserve && (
        <FoundingLightReserveModal
          isAuthed={isAuthed}
          onClose={() => setShowReserve(false)}
        />
      )}
    </div>
  );
}
